import { noop } from 'lodash';
import { useMemo } from 'react';

import { Address } from '@dreryk/address-common-api';
import { PatientGroupSimple, PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   FilterPanel,
   JustifyContent,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { PatientGroups } from '~libs.desktop.shared.patient-groups';

import { usePatientDependenciesContext } from '../../dependencies';
import { patientTranslationKeys } from '../../translations';
import { PatientFilterPanelSearchParam } from '../filters/PatientFilterPanel.types';

import { AdditionalOptionsCell } from './AdditionalOptionsCell';
import { AddressCell } from './AddressCell';
import { OptionsCell } from './OptionsCell';
import { PatientCell } from './PatientCell';
import { PhoneCell } from './PhoneCell';

type PatientTableColumnOptions = {
   onPatientRemoved: VoidFunction;
};

const showPatientCell = (bean: PatientSimple): boolean =>
   Boolean(bean.lastName || bean.firstName || bean.referenceIdentifier);

// eslint-disable-next-line max-lines-per-function
export const usePatientTableColumns = (
   options: PatientTableColumnOptions
): TableColumnConfiguration<PatientSimple>[] => {
   const { appointmentOutOfScheduleButton } = usePatientDependenciesContext();
   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<PatientSimple>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         patientGroups: '22px',
         lastName: '260px',
         phoneNumber: '150px',
         address: '100%',
         options: appointmentOutOfScheduleButton ? '325px' : '155px',
         additionalOptions: '40px',
      });

      return [
         {
            name: 'patientGroups',
            accessor: noop,
            width: columnsWidth.patientGroups,
            justifyContent: JustifyContent.CENTER,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<PatientSimple, PatientGroupSimple | undefined>) => {
               return <PatientGroups direction={'column'} patientGroups={bean.patientGroups} />;
            },
         },
         {
            name: 'lastName',
            accessor: noop,
            label: patientTranslationKeys.table.patientName,
            width: columnsWidth.lastName,

            renderer: ({ bean }: ValueRendererProps<PatientSimple, PatientSimple | undefined>) =>
               showPatientCell(bean) ? (
                  <PatientCell
                     referenceIdentifier={bean.referenceIdentifier}
                     firstName={bean.firstName}
                     id={bean.id}
                     lastName={bean.lastName}
                  />
               ) : null,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'phoneNumber',
            accessor: noop,
            label: patientTranslationKeys.table.phone,
            width: columnsWidth.phoneNumber,

            renderer: ({ bean }: ValueRendererProps<PatientSimple, PhoneNumber | undefined>) =>
               bean.phoneNumber ? <PhoneCell phoneNumber={bean.phoneNumber} /> : null,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'address',
            accessor: 'address',
            label: patientTranslationKeys.table.address,
            width: columnsWidth.address,

            renderer: ({ value }: ValueRendererProps<PatientSimple, Address | undefined>) =>
               value ? <AddressCell address={value} /> : null,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'options',
            accessor: noop,
            label: patientTranslationKeys.table.options,
            width: columnsWidth.options,

            renderer: (props: ValueRendererProps<PatientSimple, number>) => <OptionsCell patientId={props.bean.id} />,
            sortingDisabled: true,
         },
         {
            name: 'additionalOptions',
            label: '',
            width: columnsWidth.additionalOptions,
            accessor: noop,

            renderer: (props: ValueRendererProps<PatientSimple, number>) => (
               <AdditionalOptionsCell patient={props.bean} onPatientRemoved={options.onPatientRemoved} />
            ),
            sortingDisabled: true,
         },
      ] as TableColumnConfiguration<PatientSimple>[];
   }, [options.onPatientRemoved, appointmentOutOfScheduleButton]);
};

const DEFAULT_ORDERINGS: Ordering<PatientSimple>[] = [
   {
      descending: false,
      property: 'lastName',
   },
   {
      descending: false,
      property: 'firstName',
   },
];

type PatientPanelDeserializedParamsMap = {
   [PatientFilterPanelSearchParam.PATIENT_GROUPS]: 'patientGroups';
};

type PatientPanelDeserializedFilters = {
   patientGroups: number[];
};

export const usePatientTableDataConfiguration = (
   searchText: string | null
): LazyLoadedDataConfigurationByCqrsQuery<PatientSimple> => {
   const deserializedFilters = FilterPanel.useDeserializedFilters<
      PatientPanelDeserializedFilters,
      PatientPanelDeserializedParamsMap
   >({
      [PatientFilterPanelSearchParam.PATIENT_GROUPS]: (currentParamValue) => ({
         patientGroups: FilterPanel.paramToNumberArray(currentParamValue),
      }),
   });

   return useLazyLoadedDataConfigurationByCqrsQuery<PatientSimple>(() => {
      const query = new PatientSimpleQuery();
      query.freeformCriteria = Slot.emptyIfNull(searchText);
      query.patientGroupIds = Slot.emptyIfNull(
         deserializedFilters.patientGroups.length > 0 ? deserializedFilters.patientGroups : null
      );
      return query;
   }, DEFAULT_ORDERINGS);
};
